import React from "react";
import { Responsive, WidthProvider, Layout } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { generateResponsiveLayouts, GridItem } from "../chartStorage";

const ResponsiveGridLayout = WidthProvider(Responsive);

interface AdminBoardGridProps {
    items: GridItem[];
    renderItem: (item: GridItem) => React.ReactNode;
    onLayoutChange: (layout: Layout[]) => void;
    onDragStop: (layout: Layout[], oldItem: Layout, newItem: Layout, placeholder: Layout, e: MouseEvent, element: HTMLElement) => void;
    onResizeStop: (layout: Layout[], oldItem: Layout, newItem: Layout, placeholder: Layout, e: MouseEvent, element: HTMLElement) => void;
    onDrop: (layout: Layout[], item: Layout, e: Event) => void;
    droppingItem?: { i: string; w: number; h: number };
    onDrag?: (layout: Layout[], oldItem: Layout, newItem: Layout, placeholder: Layout, e: MouseEvent, element: HTMLElement) => void;
    onResize?: (layout: Layout[], oldItem: Layout, newItem: Layout, placeholder: Layout, e: MouseEvent, element: HTMLElement) => void;
    onDragStart?: (layout: Layout[], oldItem: Layout, newItem: Layout, placeholder: Layout, e: MouseEvent, element: HTMLElement) => void;
    onResizeStart?: (layout: Layout[], oldItem: Layout, newItem: Layout, placeholder: Layout, e: MouseEvent, element: HTMLElement) => void;
    showGrid?: boolean;
    isEditMode?: boolean;
}

export const AdminBoardGrid: React.FC<AdminBoardGridProps> = ({
    items,
    renderItem,
    onLayoutChange,
    onDragStop,
    onResizeStop,
    onDrop,
    droppingItem,
    onDrag,
    onResize,
    onDragStart,
    onResizeStart,
    showGrid = false,
    isEditMode = true,
}) => {
    const [rowHeight, setRowHeight] = React.useState(100);
    const [currentCols, setCurrentCols] = React.useState(12);

    // Generate responsive layouts for all breakpoints (mirrors frontend BoardGrid)
    const layouts = React.useMemo(() => {
        return generateResponsiveLayouts(items);
    }, [items]);

    const handleWidthChange = (
        containerWidth: number,
        margin: [number, number],
        cols: number,
        containerPadding: [number, number]
    ) => {
        const totalMargin = (cols - 1) * margin[0];
        const totalPadding = containerPadding ? containerPadding[0] * 2 : 0;
        const colWidth = (containerWidth - totalMargin - totalPadding) / cols;
        // Square cells: row height equals column width — same formula as frontend BoardGrid.
        setRowHeight(Math.max(50, colWidth));
        setCurrentCols(cols);
    };

    return (
        <div
            className="w-full min-h-[500px] relative transition-all duration-300"
            style={{
                "--grid-margin": "16px",
                "--grid-row-height": `${rowHeight}px`,
            } as React.CSSProperties}
        >
            {showGrid && (
                <div
                    className="absolute inset-0 z-0 grid pointer-events-none transition-opacity duration-300"
                    style={{
                        gridTemplateColumns: `repeat(${currentCols}, 1fr)`,
                        gridAutoRows: `${rowHeight}px`,
                        gap: '16px',
                        opacity: 1,
                    }}
                >
                    {Array.from({ length: currentCols * 40 }).map((_, i) => (
                        <div
                            key={i}
                            className="border border-accent-primary/10 bg-accent-primary/[0.01] rounded-xl"
                            style={{
                                transition: "border-color 0.3s ease, background-color 0.3s ease",
                            }}
                        />
                    ))}
                </div>
            )}

            <ResponsiveGridLayout
                className="layout relative z-10"
                layouts={layouts}
                breakpoints={{ xl: 1281, lg: 1025, md: 769, sm: 641, xs: 0 }}
                cols={{ xl: 24, lg: 24, md: 12, sm: 8, xs: 4 }}
                rowHeight={rowHeight}
                isDroppable={true}
                isDraggable={isEditMode}
                isResizable={isEditMode}
                onDrop={onDrop}
                onLayoutChange={onLayoutChange}
                onDragStop={onDragStop}
                onResizeStop={onResizeStop}
                onDrag={onDrag}
                onResize={onResize}
                onDragStart={onDragStart}
                onResizeStart={onResizeStart}
                onWidthChange={handleWidthChange}
                droppingItem={droppingItem}
                margin={[16, 16]}
                containerPadding={[0, 0]}
                compactType="vertical"
                preventCollision={false}
                resizeHandles={isEditMode ? ["s", "w", "e", "n", "sw", "nw", "se", "ne"] : []}
                useCSSTransforms={true}
                style={{ minHeight: "500px" }}
            >
                {items.map((item) => (
                    <div
                        key={item.i}
                        data-chart-id={item.i}
                    >
                        {renderItem(item)}
                    </div>
                ))}
            </ResponsiveGridLayout>
        </div>
    );
};
